<template>
    <div>
        <div class="horizontal">
            <div :id="'scichart-root-herd-weight-'+sid" :style="surfaceStyle"></div>
        </div>
    </div>
</template>

<script>
import { getHerdWeight } from '@/api/api';
import moment from 'moment'

import { SciChartSurface } from "scichart/Charting/Visuals/SciChartSurface";
import { NumericAxis } from "scichart/Charting/Visuals/Axis/NumericAxis";
import { EAxisAlignment } from "scichart/types/AxisAlignment";
import { FastLineRenderableSeries } from "scichart/Charting/Visuals/RenderableSeries/FastLineRenderableSeries";
import { XyDataSeries } from "scichart/Charting/Model/XyDataSeries";
import { RolloverModifier } from "scichart/Charting/ChartModifiers/RolloverModifier";
import { EllipsePointMarker } from "scichart/Charting/Visuals/PointMarkers/EllipsePointMarker";
import { chartThemeMixin } from "@/utils/chartTheme";
import { ELineDrawMode } from "scichart/Charting/Drawing/WebGlRenderContext2D"
import { NumberRange } from 'scichart/Core/NumberRange';
import { CursorModifier } from "scichart/Charting/ChartModifiers/CursorModifier";
import { XyLinearTrendFilter } from 'scichart/Charting/Model/Filters/XyLinearTrendFilter';

export default {
    mixins: [chartThemeMixin],
    props: {
        systemId: {
            required: true,
        },
        sid: {
            default: 0,
            required: false,
        },
        // Wysokosc plotna - pozwala rodzicowi zarezerwowac miejsce na wykres,
        // zeby uklad nie skakal, gdy SciChart skonczy inicjalizacje.
        height: {
            type: Number,
            default: null,
            required: false,
        }
    },
    data() {
        return {
            sciChartSurface: null,
            wasmContext: null,
            xAxis: null,
            yAxis: null,
            pointsCount: 0
        }
    },
    computed: {
        surfaceStyle() {
            const style = { width: '100%', marginRight: '10px' };

            if(this.height != null) {
                style.height = this.height + 'px';
            }

            return style;
        }
    },
    methods: {
        async getData() {
            try {
                const result = await getHerdWeight(this.systemId);
                await this.initSciChart(result);
                this.$emit('loaded', { isEmpty: this.pointsCount == 0 });
            } catch (error) {
                console.error(error);
                this.$emit('failed', error);
            }
        },
        async initSciChart(data){
            var xValues = data.labels
            var yValues = data.values

            const {sciChartSurface, wasmContext} = await SciChartSurface.create('scichart-root-herd-weight-'+this.sid);

            this.sciChartSurface = sciChartSurface;
            this.wasmContext = wasmContext;

            await sciChartSurface.applyTheme(this.chartTheme())

            this.xAxis = new NumericAxis(this.wasmContext, { axisAlignment: EAxisAlignment.Top});
            this.yAxis = new NumericAxis(this.wasmContext, { axisAlignment: EAxisAlignment.Left});

            this.yAxis.growBy = new NumberRange(0.1, 0.1);
            this.xAxis.growBy = new NumberRange(0.01, 0.01);

            sciChartSurface.xAxes.add(this.xAxis)
            sciChartSurface.yAxes.add(this.yAxis)

            this.xAxis.labelProvider.formatLabel = (unixTimestamp) => {
                return moment(unixTimestamp).format("YYYY-MM-DD")
            };

            const points = this.buildPoints(xValues, yValues);
            const bridges = this.buildGapBridges(points);
            const known = this.getKnownPoints(points);

            this.pointsCount = points.x.length;

            const lineSeries = new FastLineRenderableSeries(this.wasmContext, {
                stroke: "#0095B2",
                drawNaNAs: ELineDrawMode.DiscontinuousLine,
                strokeThickness: 2,
                pointMarker: new EllipsePointMarker(wasmContext, {
                    width: 6,
                    height: 6,
                    strokeThickness: 2,
                    fill: "#0095B230",
                    stroke: "#0095B2"
                })
            });

            lineSeries.dataSeries = new XyDataSeries(this.wasmContext, {
                xValues: points.x,
                yValues: points.y
            });

            if(points.x.length > 0) {
                lineSeries.strokeThickness = 3;
                lineSeries.rolloverModifierProps.tooltipLabelX = this.labelX ? this.labelX : "X";
                lineSeries.rolloverModifierProps.tooltipLabelY = this.labelY ? this.labelY : "Y";
                lineSeries.rolloverModifierProps.tooltipTemplate = this.tooltipTemplateMethod;
            }

            this.sciChartSurface.renderableSeries.add(lineSeries);

            // GAP LINE - dni bez danych spinamy linia przerywana, zeby bylo widac,
            // ze pomiaru nie bylo, a nie ze waga stada spadla do zera.
            let gapSeries = null;

            if(bridges.x.length > 0) {
                gapSeries = new FastLineRenderableSeries(this.wasmContext, {
                    stroke: "#0095B2",
                    strokeThickness: 2,
                    strokeDashArray: [4, 4],
                    drawNaNAs: ELineDrawMode.DiscontinuousLine,
                    dataSeries: new XyDataSeries(this.wasmContext, {
                        xValues: bridges.x,
                        yValues: bridges.y
                    })
                });

                this.sciChartSurface.renderableSeries.add(gapSeries);
            }
            // END OF GAP LINE

            // TREND LINE - z jednego pomiaru regresja liczy 0/0 i daje NaN, ktore psuje os Y.
            if(known.x.length >= 2) {
                const linearTrendFilter = new XyLinearTrendFilter(
                    new XyDataSeries(this.wasmContext, {
                        xValues: known.x,
                        yValues: known.y
                    }),
                    { dataSeriesName: this.$t('trendLine') }
                );
                const filteredLine = new FastLineRenderableSeries(this.wasmContext, {
                    dataSeries: linearTrendFilter,
                    stroke: "#ff0000",
                    strokeThickness: 2,
                    rolloverModifier: null
                });
                filteredLine.rolloverModifierProps.tooltipTemplate = this.trendTemplateMethod;

                this.sciChartSurface.renderableSeries.add(filteredLine)
            }
            // END OF TREND LINE

            const rolloverModifier = new RolloverModifier();
            rolloverModifier.showRolloverLine = false;
            this.sciChartSurface.chartModifiers.add(rolloverModifier);

            if(gapSeries != null) {
                // Punkty spinajace pokrywaja sie z prawdziwymi pomiarami - bez tego
                // ten sam dzien pokazywalby sie w dymku dwa razy.
                rolloverModifier.includeSeries(gapSeries, false);
            }

            // this.sciChartSurface.chartModifiers.add(new CursorModifier({
            //     crosshairStroke: "#0095B2",
            //     crosshairStrokeThickness: 1,
            //     tooltipContainerBackground: "#000",
            //     tooltipTextStroke: "#0095B2",
            //     showTooltip: false,
            //     axisLabelFill: "#0095B2",
            //     axisLabelStroke: "#fff",
            // }))
        },
        buildPoints(labels, values) {
            // null z API oznacza dzien bez danych - jako NaN robi w serii przerwe.
            const x = [];
            const y = [];

            if(labels == undefined || labels == null) {
                return { x, y };
            }

            for(let i = 0; i < labels.length; i++) {
                const value = values == undefined || values == null ? null : values[i];

                x.push(new Date(moment(labels[i])).getTime());
                y.push(value == undefined || value == null ? NaN : Math.round(value / 1000));
            }

            return { x, y };
        },
        buildGapBridges(points) {
            // Kazda przerwe spinamy odcinkiem od ostatniego znanego punktu do pierwszego kolejnego.
            // Separator miedzy odcinkami to NaN tylko w Y - X musi zostac prawdziwa liczba,
            // bo SciChart bierze zakres osi X z pierwszego i ostatniego X; NaN w X psuje
            // cala os ("Argument min must be a real number").
            const x = [];
            const y = [];
            let lastKnown = -1;

            for(let i = 0; i < points.y.length; i++) {
                if(isNaN(points.y[i])) {
                    continue;
                }

                if(lastKnown >= 0 && i - lastKnown > 1) {
                    x.push(points.x[lastKnown], points.x[i], points.x[i]);
                    y.push(points.y[lastKnown], points.y[i], NaN);
                }

                lastKnown = i;
            }

            return { x, y };
        },
        getKnownPoints(points) {
            // Linia trendu liczona tylko z dni, ktore faktycznie maja pomiar.
            const x = [];
            const y = [];

            for(let i = 0; i < points.y.length; i++) {
                if(isNaN(points.y[i])) {
                    continue;
                }

                x.push(points.x[i]);
                y.push(points.y[i]);
            }

            return { x, y };
        },
        tooltipTemplateMethod(id, seriesInfo, rolloverTooltip) {
            const colors = this.tooltipColors();
            // Rozmiar zgłaszany SciChartowi = prostokąt z obrysem. Dawne 200x120
            // odsuwało dymek od punktu w lewo i w górę, poza wąski wykres.
            const width = 152;
            const height = 42;
            rolloverTooltip.updateSize(width, height);

            return `<svg width="${width}" height="${height}">
                <rect x="1" y="1" height="40" width="150" rx="5" style="stroke-width:1; stroke:${colors.border}" fill="${colors.background}"/>
                <svg>
                    <text font-size="13" font-family="Verdana" fill="${colors.text}">
                        <tspan x="11" dy="1.2em">${moment(Math.floor(seriesInfo.formattedXValue)).format("DD-MM-YYYY")}</tspan>
                        <tspan x="11" dy="1.2em">${parseFloat(seriesInfo.formattedYValue).toFixed(2)} kg</tspan>
                    </text>
                    <defs>
                        <filter id="f1" x="0" y="0" width="200%" height="200%">
                        <feOffset result="offOut" in="SourceGraphic" dx="20" dy="20" />
                        <feBlend in="SourceGraphic" in2="offOut" mode="normal" />
                        </filter>
                    </defs>
                </svg>
                </svg>`;
        },
        trendTemplateMethod(id, seriesInfo, rolloverTooltip) {
            const { tooltipColor } = rolloverTooltip.tooltipProps;
            const colors = this.tooltipColors();
            const width = 152;
            const height = 32;
            rolloverTooltip.updateSize(width, height);

            return `<svg width="${width}" height="${height}">
                <rect x="1" y="1" height="30" width="150" rx="5" style="stroke-width:2; stroke:${tooltipColor}" fill="${colors.background}"/>
                <svg>
                    <text font-size="13" font-family="Verdana" fill="${colors.text}">
                        <tspan x="11" dy="1.2em">${seriesInfo.formattedYValue} kg</tspan>
                    </text>
                    <defs>
                    <filter id="f1" x="0" y="0" width="200%" height="200%">
                        <feOffset result="offOut" in="SourceGraphic" dx="20" dy="20" />
                        <feBlend in="SourceGraphic" in2="offOut" mode="normal" />
                    </filter>
                    </defs>
                </svg>
            </svg>`;
        },
    },
    mounted: async function() {
        this.getData();
    },
    beforeDestroy: async function() {
        if(this.sciChartSurface || this.wasmContext){
            this.sciChartSurface = null
            this.wasmContext = null
        }
        this.sciChartSurface = null
        this.wasmContext = null
    }
}
</script>

<style scoped>

</style>


// WEBPACK FOOTER //
// src/components/Charts/SciChartHerdWeight.vue